<template>
  <Teleport to="body">
    <Transition name="webinar-registrants-modal">
      <div v-if="open" class="fixed inset-0 z-[90] flex items-center justify-center bg-zinc-950/60 p-4 backdrop-blur-sm" @click.self="close">
        <section class="flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="webinar-registrants-title">
          <header class="flex items-start justify-between border-b border-zinc-200 px-6 py-5">
            <div class="min-w-0">
              <p class="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">Webinar registrations</p>
              <h2 id="webinar-registrants-title" class="mt-1 truncate text-xl font-bold text-zinc-950">{{ event?.title || 'Webinar event' }}</h2>
            </div>
            <button type="button" class="ml-4 rounded-xl p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900" aria-label="Close" @click="close">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>

          <div class="grid grid-cols-1 gap-3 border-b border-zinc-100 bg-zinc-50/70 p-5 sm:grid-cols-3">
            <div class="rounded-2xl border border-zinc-200 bg-white p-4">
              <p class="text-xs font-semibold text-zinc-500">Registrants</p>
              <p class="mt-1 text-2xl font-bold text-zinc-950">{{ summary.registrationCount }}</p>
            </div>
            <div class="rounded-2xl border border-zinc-200 bg-white p-4">
              <p class="text-xs font-semibold text-zinc-500">Show-ups</p>
              <p class="mt-1 text-2xl font-bold text-zinc-950">{{ summary.showUpCount }}</p>
            </div>
            <div class="rounded-2xl border border-zinc-200 bg-white p-4">
              <p class="text-xs font-semibold text-zinc-500">Conversion rate</p>
              <p class="mt-1 text-2xl font-bold text-zinc-950">{{ summary.conversionRate }}%</p>
            </div>
          </div>

          <div v-if="loading" class="space-y-3 overflow-hidden p-5" aria-label="Loading registrants">
            <div v-for="row in 5" :key="row" class="flex items-center gap-3 rounded-2xl border border-zinc-100 p-3">
              <div class="h-11 w-11 animate-pulse rounded-full bg-zinc-200" />
              <div class="flex-1"><div class="h-4 w-40 animate-pulse rounded bg-zinc-200" /><div class="mt-2 h-3 w-28 animate-pulse rounded bg-zinc-100" /></div>
            </div>
          </div>
          <div v-else-if="error" class="p-8 text-center">
            <p class="text-sm font-semibold text-red-700">{{ error }}</p>
            <button type="button" class="mt-4 text-sm font-bold text-blue-600 hover:text-blue-700" @click="load">Try again</button>
          </div>
          <div v-else-if="members.length" class="registrants-scroll min-h-0 flex-1 overflow-y-auto p-5">
            <button
              v-for="member in members"
              :key="member.memberId"
              type="button"
              class="mb-2 flex w-full items-center gap-3 rounded-2xl border border-transparent p-3 text-left transition hover:border-blue-100 hover:bg-blue-50/60"
              @click="openMember(member.memberId)"
            >
              <img v-if="member.avatarUrl" :src="member.avatarUrl" :alt="member.name" class="h-11 w-11 shrink-0 rounded-full bg-zinc-100 object-cover" @error="useDefaultAvatar" />
              <span v-else class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">{{ initials(member.name) }}</span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-bold text-zinc-900">{{ member.name }}</span>
                <span class="mt-0.5 block text-xs text-zinc-500">Registered {{ formatDate(member.registeredAt) }}</span>
              </span>
              <span class="text-right">
                <span class="block text-xs font-bold" :class="member.attended ? 'text-emerald-600' : 'text-zinc-400'">{{ member.attended ? 'Showed up' : 'Not attended' }}</span>
                <span v-if="member.attended" class="mt-1 block text-xs text-zinc-500">{{ formatDuration(member.totalWatchSeconds) }}</span>
              </span>
              <ChevronRightIcon class="h-4 w-4 shrink-0 text-zinc-400" />
            </button>
          </div>
          <div v-else class="flex min-h-[260px] flex-col items-center justify-center p-8 text-center">
            <UsersIcon class="h-10 w-10 text-zinc-300" />
            <p class="mt-3 text-sm font-bold text-zinc-800">No registrations yet</p>
            <p class="mt-1 text-sm text-zinc-500">Members will appear here as they register.</p>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ChevronRightIcon, UsersIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import defaultAvatar from '@/assets/media/images/default-avatar.png';
import { useWebinarStore } from '@/stores/webinarStore';

const props = defineProps({ open: Boolean, event: { type: Object, default: null } });
const emit = defineEmits(['close']);
const router = useRouter();
const store = useWebinarStore();
const error = ref('');
const payload = computed(() => store.registrationsByEvent[String(props.event?.id || '')] || {});
const members = computed(() => Array.isArray(payload.value?.members) ? payload.value.members : []);
const summary = computed(() => payload.value?.summary || {
  registrationCount: Number(props.event?.registrations || 0), showUpCount: Number(props.event?.showUpCount || 0), conversionRate: Number(props.event?.conversionRate || 0),
});
const loading = computed(() => Boolean(store.loadingRegistrationsEventIds[String(props.event?.id || '')]));

async function load() {
  if (!props.event?.id || props.event?.isPrototype) return;
  error.value = '';
  try { await store.fetchRegistrations(props.event.id, { force: true }); }
  catch (requestError) { error.value = String(requestError?.message || 'Unable to load Webinar registrations.'); }
}
function close() { emit('close'); }
function openMember(memberId) { close(); router.push({ name: 'MemberDetails', params: { memberId } }); }
function initials(name) { return String(name || 'M').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(); }
function useDefaultAvatar(event) {
  const image = event?.currentTarget;
  if (image && image.src !== defaultAvatar) image.src = defaultAvatar;
}
function formatDate(value) { const date = new Date(value); return Number.isNaN(date.getTime()) ? 'recently' : date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
function formatDuration(seconds) { const total = Math.max(0, Number(seconds || 0)); const hours = Math.floor(total / 3600); const minutes = Math.floor((total % 3600) / 60); return hours ? `${hours}h ${minutes}m watched` : `${minutes}m watched`; }

watch(() => [props.open, props.event?.id], ([isOpen]) => { if (isOpen) load(); }, { immediate: true });
</script>

<style scoped>
.registrants-scroll { scrollbar-width: thin; scrollbar-color: #a1a1aa transparent; }
.registrants-scroll::-webkit-scrollbar { width: 6px; }
.registrants-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: #a1a1aa; }
.webinar-registrants-modal-enter-active, .webinar-registrants-modal-leave-active { transition: opacity .2s ease; }
.webinar-registrants-modal-enter-from, .webinar-registrants-modal-leave-to { opacity: 0; }
</style>
